import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PageLoader } from "@/components/shared";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";

import AdminDashboard from "@/pages/admin/AdminDashboard";
import Authors from "@/pages/admin/Authors";
import Books from "@/pages/admin/Books";
import SalesManager from "@/pages/admin/SalesManager";
import Redemptions from "@/pages/admin/Redemptions";
import ReprintOrders from "@/pages/admin/ReprintOrders";
import Tickets from "@/pages/admin/Tickets";
import Marketing from "@/pages/admin/Marketing";
import AdminFaqs from "@/pages/admin/Faqs";
import Settings from "@/pages/admin/Settings";

import AuthorDashboard from "@/pages/author/AuthorDashboard";
import Redeem from "@/pages/author/Redeem";
import Reprint from "@/pages/author/Reprint";
import Support from "@/pages/author/Support";
import Profile from "@/pages/author/Profile";
import AuthorMarketing from "@/pages/author/Marketing";
import AuthorFaqs from "@/pages/author/Faqs";

import {
  LayoutDashboard, Users, Library, TrendingUp, Wallet, Printer, LifeBuoy, UserCog, Megaphone, HelpCircle, Settings as SettingsIcon,
} from "lucide-react";

const adminNav = [
  { to: "/admin", end: true, label: "Overview", icon: LayoutDashboard, testid: "overview" },
  { to: "/admin/authors", label: "Authors", icon: Users, testid: "authors" },
  { to: "/admin/books", label: "Books", icon: Library, testid: "books" },
  { to: "/admin/sales", label: "Sales", icon: TrendingUp, testid: "sales" },
  { to: "/admin/redemptions", label: "Redemptions", icon: Wallet, testid: "redemptions" },
  { to: "/admin/orders", label: "Reprint Orders", icon: Printer, testid: "orders" },
  { to: "/admin/marketing", label: "Marketing", icon: Megaphone, testid: "marketing" },
  { to: "/admin/faqs", label: "FAQs", icon: HelpCircle, testid: "faqs" },
  { to: "/admin/support", label: "Support", icon: LifeBuoy, testid: "support" },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon, testid: "settings" },
];

const authorNav = [
  { to: "/author", end: true, label: "Dashboard", icon: LayoutDashboard, testid: "dashboard" },
  { to: "/author/redeem", label: "Redemptions", icon: Wallet, testid: "redeem" },
  { to: "/author/reprint", label: "Order Reprints", icon: Printer, testid: "reprint" },
  { to: "/author/marketing", label: "Marketing", icon: Megaphone, testid: "marketing" },
  { to: "/author/faqs", label: "FAQs", icon: HelpCircle, testid: "faqs" },
  { to: "/author/support", label: "Support", icon: LifeBuoy, testid: "support" },
  { to: "/author/profile", label: "My Profile", icon: UserCog, testid: "profile" },
];

function Protected({ role, children }) {
  const { user, ready } = useAuth();
  if (!ready || user === null) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/author"} replace />;
  }
  return children;
}

function LandingRedirect() {
  const { user, ready } = useAuth();
  if (!ready || user === null) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "admin" ? "/admin" : "/author"} replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors closeButton />
          <Routes>
            <Route path="/" element={<LandingRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            <Route
              element={
                <Protected role="admin">
                  <Layout nav={adminNav} title="Admin Panel" />
                </Protected>
              }
            >
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/authors" element={<Authors />} />
              <Route path="/admin/books" element={<Books />} />
              <Route path="/admin/sales" element={<SalesManager />} />
              <Route path="/admin/redemptions" element={<Redemptions />} />
              <Route path="/admin/orders" element={<ReprintOrders />} />
              <Route path="/admin/marketing" element={<Marketing />} />
              <Route path="/admin/faqs" element={<AdminFaqs />} />
              <Route path="/admin/support" element={<Tickets />} />
              <Route path="/admin/settings" element={<Settings />} />
            </Route>

            <Route
              element={
                <Protected role="author">
                  <Layout nav={authorNav} title="Author Dashboard" />
                </Protected>
              }
            >
              <Route path="/author" element={<AuthorDashboard />} />
              <Route path="/author/redeem" element={<Redeem />} />
              <Route path="/author/reprint" element={<Reprint />} />
              <Route path="/author/marketing" element={<AuthorMarketing />} />
              <Route path="/author/faqs" element={<AuthorFaqs />} />
              <Route path="/author/support" element={<Support />} />
              <Route path="/author/profile" element={<Profile />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
